"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MERGE_ITEM_KEY = exports.NameStandardInputSchema = exports.ChangeTypeInputSchema = exports.ToolRunItemResultSchema = exports.ToolRunItemStateSchema = exports.ToolRunSourceSchema = exports.ToolRunStatusSchema = exports.ConvertibleTypeIdSchema = exports.CONVERTIBLE_TYPE_IDS = exports.WR_CONFIRMATIONS = exports.WrClientEventInputSchema = exports.WrSubmitInputSchema = exports.WrScorecardSchema = exports.WrSummarySchema = exports.WrSettingsSchema = exports.WrShiftSchema = exports.WrMyStatsSchema = exports.WrHistoryDaySchema = exports.WrMessageInputSchema = exports.WrNotificationSettingsSchema = exports.WrPickStrategySchema = exports.WrEventSchema = exports.WrEventTypeSchema = exports.WrAssignmentSchema = exports.WrPhotoEvidenceSchema = exports.WrFieldChangeSchema = exports.WrSnapshotSchema = exports.WrAssignmentStatusSchema = exports.WrRedFlagSchema = exports.WR_RED_FLAGS = exports.WrRedFlagCodeSchema = exports.WrItemSchema = exports.WrItemStatusSchema = exports.WrIssueSchema = exports.WR_ISSUES = exports.WrIssueCodeSchema = exports.MyToolsResponseSchema = exports.ToolLibraryEntrySchema = exports.ToolActivitySchema = exports.ToolGrantInputSchema = exports.ToolGrantSchema = exports.TOOL_REGISTRY = exports.TOOL_GROUP_LABELS = exports.TOOL_PRODUCT_CONVERSION = exports.TOOL_NAME_STANDARD = exports.TOOL_CONVERT_TYPE = exports.TOOL_WEB_READINESS = exports.ToolDefinitionSchema = exports.ToolStatusSchema = exports.ToolGroupSchema = void 0;
exports.ToolRunItemPatchSchema = exports.ToolRunItemSchema = exports.ToolRunVariantChangeSchema = exports.ToolRunSchema = exports.ToolRunCountsSchema = exports.ToolRunCreateInputSchema = exports.MergePlanSchema = exports.MergeIntegritySchema = exports.MergeStepSchema = exports.MergePromotionSchema = exports.MergeConflictSchema = exports.MergeRowSchema = exports.MergeInputSchema = exports.MergeTargetSchema = exports.MergeSourceSchema = exports.MERGE_STEPS = exports.MERGE_MAX_VARIANTS = void 0;
exports.toolKeyForSelfServiceRoute = toolKeyForSelfServiceRoute;
exports.getToolDefinition = getToolDefinition;
exports.defaultWrSettings = defaultWrSettings;
const zod_1 = require("zod");
/**
 * Internal Tools — operational tools RubyTill builds for its own stores first
 * and can switch on for any tenant from Super Admin → Tools → Tool Library.
 *
 * Tools are NOT packages: no Stripe item, never billed, never in the catalog,
 * signup or merchant Plans & Billing. A tool is on for a tenant when a
 * ToolGrant row exists with `enabled: true`.
 *
 * First tool: Web Readiness Review — redo the whole catalog ONCE before going
 * online. Each day every assigned employee gets N items (owner sets N), fixes
 * them in the normal product editor and ticks "web ready". An item that is web
 * ready is never assigned again. The owner sees done / total and a track record
 * of what each employee actually changed.
 */
// ── Tool registry ─────────────────────────────────────────────────────────────
exports.ToolGroupSchema = zod_1.z.enum(['catalog_cleanup', 'import_migration']);
exports.ToolStatusSchema = zod_1.z.enum(['live', 'in_build', 'planned']);
exports.ToolDefinitionSchema = zod_1.z.object({
    key: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1),
    description: zod_1.z.string(),
    group: exports.ToolGroupSchema,
    status: exports.ToolStatusSchema,
    /** platform-app hash route the tool opens (live tools only). */
    merchantRoute: zod_1.z.string().optional(),
    /**
     * Employee self-service page for this tool (added in Super Admin → Menus like
     * My Commission). The merchant sidebar shows that menu item ONLY while the
     * tool is enabled for the tenant.
     */
    selfServiceRoute: zod_1.z.string().optional(),
    /** lucide icon name, for both UIs. */
    icon: zod_1.z.string(),
    /** Plug-in contract labels shown on the library card (T0). */
    takes: zod_1.z.string().optional(),
    gives: zod_1.z.string().optional(),
    plugsInto: zod_1.z.array(zod_1.z.string()).optional(),
    /** Other tool keys this tool can launch. */
    canUse: zod_1.z.array(zod_1.z.string()).optional(),
    /** Shows a "New" badge in the library. */
    isNew: zod_1.z.boolean().optional(),
});
exports.TOOL_WEB_READINESS = 'web-readiness';
exports.TOOL_CONVERT_TYPE = 'convert-product-type';
exports.TOOL_NAME_STANDARD = 'name-standard';
exports.TOOL_PRODUCT_CONVERSION = 'product-conversion';
exports.TOOL_GROUP_LABELS = {
    catalog_cleanup: 'Catalog cleanup',
    import_migration: 'Import & migration',
};
/** Every tool we have or plan. Add a row here to add a tool to the library. */
exports.TOOL_REGISTRY = [
    {
        key: exports.TOOL_WEB_READINESS,
        name: 'Web Readiness Review',
        description: 'Daily batches of products for employees to verify, photograph and describe in the product editor, until the whole catalog is ready to sell online. Owner sees done / total and a track record of every change.',
        group: 'catalog_cleanup',
        status: 'live',
        merchantRoute: '/tools/web-readiness',
        selfServiceRoute: '/pos/my-web-readiness',
        icon: 'badge-check',
        canUse: [exports.TOOL_CONVERT_TYPE, exports.TOOL_NAME_STANDARD],
    },
    {
        key: exports.TOOL_CONVERT_TYPE,
        name: 'Convert Product Type',
        description: 'Search and collect products, then change their type. Barcodes, location prices, stock and history stay with every variant.',
        group: 'catalog_cleanup', status: 'live', merchantRoute: '/tools/convert-product-type', icon: 'arrow-right-left',
        takes: 'products', gives: 'converted products + run report', plugsInto: ['Web Readiness', 'Workflows'], isNew: true,
    },
    {
        key: exports.TOOL_NAME_STANDARD,
        name: 'Name Standard',
        description: 'Rebuilds product and variant names as Brand › Product › options. Preview every change, approve, apply. Past sales and orders keep the name they were made with.',
        group: 'catalog_cleanup', status: 'live', merchantRoute: '/tools/name-standard', icon: 'type',
        takes: 'products', gives: 'renamed products + run report', plugsInto: ['Web Readiness', 'Convert Product Type', 'Workflows'], isNew: true,
    },
    {
        key: exports.TOOL_PRODUCT_CONVERSION,
        name: 'Product Conversion',
        description: 'Merge products or single variants into one Product with Options — new or existing. Variant ids, barcodes, prices, stock and history move intact; the old products are archived and removed from every search.',
        group: 'catalog_cleanup', status: 'live', merchantRoute: '/tools/convert-product-type', icon: 'merge',
        takes: 'products or variants', gives: 'one Product with Options + run report', plugsInto: ['Convert Product Type', 'Web Readiness'], isNew: true,
    },
    {
        // Value mirrors TOOL_DATA_MIGRATION in migration.schema.ts.
        key: 'data-migration',
        name: 'Data Migration',
        description: 'Bring a new client over from Clover, Square, Lightspeed, Toast or any spreadsheet. The client uploads their exports; we map, dry-run, back up, approve and import products, barcodes, prices and stock — and can roll the whole import back.',
        group: 'import_migration', status: 'live', merchantRoute: '/tools/data-migration', icon: 'database',
        takes: 'export files from another POS', gives: 'products, variants, barcodes, prices and stock + run report', isNew: true,
    },
    { key: 'import-square', name: 'Square import', description: 'Products, variants, images, customers and gift cards from a Square account.', group: 'import_migration', status: 'planned', icon: 'square' },
    { key: 'import-clover', name: 'Clover import', description: 'Inventory, modifiers, employees and customers from Clover.', group: 'import_migration', status: 'planned', icon: 'leaf' },
    { key: 'import-lightspeed', name: 'Lightspeed import', description: 'Catalog and stock by location from Lightspeed.', group: 'import_migration', status: 'planned', icon: 'zap' },
    { key: 'import-csv-shopify', name: 'CSV / Shopify import', description: 'A mapped spreadsheet or Shopify export, with image fetch.', group: 'import_migration', status: 'planned', icon: 'file-spreadsheet' },
];
/** Self-service route → tool key (only tools that declare one). */
function toolKeyForSelfServiceRoute(route) {
    const r = String(route || '').split('?')[0].replace(/\/+$/, '');
    return exports.TOOL_REGISTRY.find((t) => t.selfServiceRoute && t.selfServiceRoute === r)?.key;
}
function getToolDefinition(key) {
    return exports.TOOL_REGISTRY.find((t) => t.key === key);
}
// ── Grants (table: {prefix}-tool-grants-{stage}, PK tenantId, SK toolKey) ─────
exports.ToolGrantSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1),
    toolKey: zod_1.z.string().min(1),
    enabled: zod_1.z.boolean(),
    enabledBy: zod_1.z.string().optional(),
    enabledByEmail: zod_1.z.string().optional(),
    enabledAt: zod_1.z.string().optional(),
    disabledBy: zod_1.z.string().optional(),
    disabledAt: zod_1.z.string().optional(),
    /** Tenant display name, denormalised for the library list. */
    tenantName: zod_1.z.string().optional(),
    /** Tool-specific settings (Web Readiness stores WebReadinessSettings here). */
    settings: zod_1.z.record(zod_1.z.unknown()).optional(),
    updatedAt: zod_1.z.string(),
});
exports.ToolGrantInputSchema = zod_1.z.object({ enabled: zod_1.z.boolean() });
/** Super-admin audit trail (table: {prefix}-tool-activity-{stage}). */
exports.ToolActivitySchema = zod_1.z.object({
    activityId: zod_1.z.string(),
    toolKey: zod_1.z.string(),
    tenantId: zod_1.z.string(),
    tenantName: zod_1.z.string().optional(),
    action: zod_1.z.enum(['enabled', 'disabled']),
    actorId: zod_1.z.string(),
    actorEmail: zod_1.z.string().optional(),
    at: zod_1.z.string(),
});
exports.ToolLibraryEntrySchema = exports.ToolDefinitionSchema.extend({
    tenantCount: zod_1.z.number().int(),
});
/** GET /tools/my — which tools the caller's tenant has. */
exports.MyToolsResponseSchema = zod_1.z.object({
    tools: zod_1.z.array(exports.ToolDefinitionSchema),
});
// ── Web Readiness ─────────────────────────────────────────────────────────────
/** Catalog problems the nightly scan detects. `blocking` issues must be gone
 *  before an item can be ticked (or overridden with a reason, which red-flags). */
exports.WrIssueCodeSchema = zod_1.z.enum([
    'variant_missing_image',
    'no_main_image',
    'description_junk',
    'description_short',
    'title_caps',
    'no_category',
    'no_brand',
    'duplicate_title',
    'no_price',
]);
exports.WR_ISSUES = {
    variant_missing_image: { label: 'Variants without a photo', weight: 30, blocking: true },
    no_main_image: { label: 'No main photo', weight: 25, blocking: true },
    description_junk: { label: 'Description has old-system formatting', weight: 15, blocking: true },
    description_short: { label: 'Description under 40 words', weight: 10, blocking: true },
    title_caps: { label: 'Title ALL CAPS', weight: 8, blocking: true },
    no_category: { label: 'No category', weight: 8, blocking: true },
    no_brand: { label: 'No brand', weight: 3, blocking: false },
    duplicate_title: { label: 'Duplicate title', weight: 3, blocking: false },
    no_price: { label: 'No price', weight: 10, blocking: false },
};
exports.WrIssueSchema = zod_1.z.object({
    code: exports.WrIssueCodeSchema,
    /** e.g. "11 of 14 variants" */
    detail: zod_1.z.string().optional(),
    variantIds: zod_1.z.array(zod_1.z.string()).optional(),
});
exports.WrItemStatusSchema = zod_1.z.enum([
    'not_started',
    'assigned',
    'in_progress',
    'awaiting_review',
    'web_ready',
    'sent_back',
    'excluded',
]);
/** One row per product (table: {prefix}-wr-items-{stage}, PK tenantId, SK productId). */
exports.WrItemSchema = zod_1.z.object({
    tenantId: zod_1.z.string(),
    productId: zod_1.z.string(),
    productName: zod_1.z.string(),
    imageUrl: zod_1.z.string().optional(),
    variantCount: zod_1.z.number().int().default(0),
    status: exports.WrItemStatusSchema,
    issues: zod_1.z.array(exports.WrIssueSchema).default([]),
    issueScore: zod_1.z.number().default(0),
    scannedAt: zod_1.z.string().optional(),
    assignmentId: zod_1.z.string().optional(),
    assigneeId: zod_1.z.string().optional(),
    assigneeName: zod_1.z.string().optional(),
    verifiedBy: zod_1.z.string().optional(),
    verifiedByName: zod_1.z.string().optional(),
    verifiedAt: zod_1.z.string().optional(),
    /** Set by the nightly re-check on web-ready items; reported, never re-queued. */
    rescanIssues: zod_1.z.array(exports.WrIssueSchema).optional(),
    excludedReason: zod_1.z.string().optional(),
    updatedAt: zod_1.z.string(),
});
exports.WrRedFlagCodeSchema = zod_1.z.enum([
    'duplicate_photo',
    'too_fast',
    'no_changes',
    'override_used',
    'library_photos',
    'opened_no_save',
]);
exports.WR_RED_FLAGS = {
    duplicate_photo: { label: 'Same photo reused on 2+ variants', defaultOn: true },
    too_fast: { label: 'Under 2 min per item (or a third of the shop average)', defaultOn: true },
    no_changes: { label: 'Ticked with no saved changes while issues existed', defaultOn: true },
    override_used: { label: 'Ticked with a check overridden', defaultOn: true },
    library_photos: { label: 'Photos picked from the library instead of the camera', defaultOn: false },
    opened_no_save: { label: 'Opened, nothing saved, day ended', defaultOn: true },
};
exports.WrRedFlagSchema = zod_1.z.object({
    code: exports.WrRedFlagCodeSchema,
    detail: zod_1.z.string(),
});
exports.WrAssignmentStatusSchema = zod_1.z.enum([
    'assigned',
    'in_progress',
    'awaiting_review',
    'web_ready',
    'sent_back',
    'flagged_by_employee',
    'skipped',
    'reassigned',
    'expired',
]);
/** Server-captured product state used to diff what the employee really changed. */
exports.WrSnapshotSchema = zod_1.z.object({
    name: zod_1.z.string().optional(),
    description: zod_1.z.string().optional(),
    categoryId: zod_1.z.string().optional(),
    brandId: zod_1.z.string().optional(),
    images: zod_1.z.array(zod_1.z.string()).default([]),
    variants: zod_1.z.array(zod_1.z.object({
        variantId: zod_1.z.string(),
        name: zod_1.z.string().optional(),
        images: zod_1.z.array(zod_1.z.string()).default([]),
        price: zod_1.z.number().optional(),
    })).default([]),
    takenAt: zod_1.z.string(),
});
exports.WrFieldChangeSchema = zod_1.z.object({
    field: zod_1.z.string(),
    label: zod_1.z.string(),
    before: zod_1.z.string().optional(),
    after: zod_1.z.string().optional(),
    variantId: zod_1.z.string().optional(),
});
exports.WrPhotoEvidenceSchema = zod_1.z.object({
    url: zod_1.z.string(),
    variantId: zod_1.z.string().optional(),
    variantName: zod_1.z.string().optional(),
    /** 'camera' | 'library' when the client knows, else 'unknown'. */
    source: zod_1.z.enum(['camera', 'library', 'unknown']).default('unknown'),
    /** Content fingerprint (S3 ETag / size) — equal fingerprints = same file. */
    fingerprint: zod_1.z.string().optional(),
    duplicateOf: zod_1.z.array(zod_1.z.string()).optional(),
    addedAt: zod_1.z.string(),
});
/** table: {prefix}-wr-assignments-{stage}, PK tenantId, SK assignmentId */
exports.WrAssignmentSchema = zod_1.z.object({
    tenantId: zod_1.z.string(),
    assignmentId: zod_1.z.string(),
    /** YYYY-MM-DD in the tenant's timezone. */
    date: zod_1.z.string(),
    userId: zod_1.z.string(),
    userName: zod_1.z.string().optional(),
    productId: zod_1.z.string(),
    productName: zod_1.z.string(),
    imageUrl: zod_1.z.string().optional(),
    status: exports.WrAssignmentStatusSchema,
    issuesAtAssign: zod_1.z.array(exports.WrIssueSchema).default([]),
    assignedBy: zod_1.z.enum(['auto', 'manual']).default('auto'),
    assignedAt: zod_1.z.string(),
    openedAt: zod_1.z.string().optional(),
    activeMs: zod_1.z.number().default(0),
    saveCount: zod_1.z.number().int().default(0),
    snapshotStart: exports.WrSnapshotSchema.optional(),
    snapshotLast: exports.WrSnapshotSchema.optional(),
    changes: zod_1.z.array(exports.WrFieldChangeSchema).default([]),
    photos: zod_1.z.array(exports.WrPhotoEvidenceSchema).default([]),
    confirmations: zod_1.z.array(zod_1.z.string()).default([]),
    overrides: zod_1.z.array(zod_1.z.object({ code: exports.WrIssueCodeSchema, reason: zod_1.z.string() })).default([]),
    redFlags: zod_1.z.array(exports.WrRedFlagSchema).default([]),
    submittedAt: zod_1.z.string().optional(),
    reviewedBy: zod_1.z.string().optional(),
    reviewedByName: zod_1.z.string().optional(),
    reviewedAt: zod_1.z.string().optional(),
    reviewNote: zod_1.z.string().optional(),
    employeeNote: zod_1.z.string().optional(),
    /** Fixes the approving manager made in the product editor after the employee submitted (diff vs snapshotLast). */
    managerChanges: zod_1.z.array(exports.WrFieldChangeSchema).optional(),
    /** Submitted with changes waiting for Publish — the approving manager publishes them. */
    needsPublish: zod_1.z.boolean().optional(),
    /** Times this item was sent back (right-first-time stat). */
    sentBackCount: zod_1.z.number().int().optional(),
    /** Given by an automatic top-up after the person finished their batch. */
    topUp: zod_1.z.boolean().optional(),
    /** Item thread (messages live in wr-events, type 'message'); counters for unread badges. */
    messageCount: zod_1.z.number().int().optional(),
    lastMessageAt: zod_1.z.string().optional(),
    lastMessageBy: zod_1.z.string().optional(),
    lastMessageByName: zod_1.z.string().optional(),
    lastMessagePreview: zod_1.z.string().optional(),
    /** Read markers: the assignee, and any manager (shared). */
    employeeReadAt: zod_1.z.string().optional(),
    managerReadAt: zod_1.z.string().optional(),
    updatedAt: zod_1.z.string(),
});
exports.WrEventTypeSchema = zod_1.z.enum([
    'assigned', 'opened', 'saved', 'photo_added', 'submitted', 'approved', 'sent_back',
    'reassigned', 'skipped', 'flagged', 'rescan_failed', 'expired',
    'message', 'topped_up', 'batch_assigned',
]);
/** Append-only track record (table: {prefix}-wr-events-{stage}). */
exports.WrEventSchema = zod_1.z.object({
    tenantId: zod_1.z.string(),
    eventId: zod_1.z.string(),
    at: zod_1.z.string(),
    type: exports.WrEventTypeSchema,
    userId: zod_1.z.string(),
    userName: zod_1.z.string().optional(),
    productId: zod_1.z.string(),
    productName: zod_1.z.string().optional(),
    assignmentId: zod_1.z.string().optional(),
    summary: zod_1.z.string(),
    changes: zod_1.z.array(exports.WrFieldChangeSchema).optional(),
    redFlags: zod_1.z.array(exports.WrRedFlagSchema).optional(),
    /** type 'message': who wrote it. */
    authorRole: zod_1.z.enum(['manager', 'employee']).optional(),
});
exports.WrPickStrategySchema = zod_1.z.enum(['worst_first', 'random', 'best_sellers', 'in_stock']);
exports.WrNotificationSettingsSchema = zod_1.z.object({
    /** Push to the employee: batch / top-up assigned. */
    pushAssigned: zod_1.z.boolean().default(true),
    /** Push to the employee: sent back, or a manager wrote on their item. */
    pushToEmployee: zod_1.z.boolean().default(true),
    /** Push to managers: flagged, red flag on submit, or an employee wrote. */
    pushToManagers: zod_1.z.boolean().default(true),
    /** Email: the employee's batch at its first assignment of the day + the owner's morning digest. */
    emailDigest: zod_1.z.boolean().default(true),
    /**
     * Email on item activity: every message (both directions), send-backs to the
     * employee, flags and red flags to managers. Each person can still untick the
     * 'webReadiness' email category in their notification settings.
     */
    emailActivity: zod_1.z.boolean().default(true),
});
exports.WrMessageInputSchema = zod_1.z.object({ text: zod_1.z.string().trim().min(1).max(1000) });
/** One day in an employee's My View history. */
exports.WrHistoryDaySchema = zod_1.z.object({
    date: zod_1.z.string(),
    total: zod_1.z.number().int(),
    done: zod_1.z.number().int(),
    approved: zod_1.z.number().int(),
    sentBack: zod_1.z.number().int(),
    rolledOver: zod_1.z.number().int(),
    topUps: zod_1.z.number().int(),
});
exports.WrMyStatsSchema = zod_1.z.object({
    weekDone: zod_1.z.number().int(),
    /** Consecutive assigned days (newest first) where the whole batch was finished. */
    streak: zod_1.z.number().int(),
    avgMinutesPerItem: zod_1.z.number(),
    shopAvgMinutesPerItem: zod_1.z.number().optional(),
    /** % of submitted items in 30 days never sent back. */
    firstTimePct: zod_1.z.number().int(),
});
exports.WrShiftSchema = zod_1.z.object({
    /** "HH:MM" local */
    startTime: zod_1.z.string().optional(),
    endTime: zod_1.z.string().optional(),
    hours: zod_1.z.number(),
});
exports.WrSettingsSchema = zod_1.z.object({
    itemsPerPersonPerDay: zod_1.z.number().int().min(1).max(50).default(10),
    assigneeIds: zod_1.z.array(zod_1.z.string()).default([]),
    assigneeNames: zod_1.z.record(zod_1.z.string()).default({}),
    /** 0 = Sunday … 6 = Saturday */
    days: zod_1.z.array(zod_1.z.number().int().min(0).max(6)).default([1, 2, 3, 4, 5, 6]),
    assignHour: zod_1.z.number().int().min(0).max(23).default(8),
    timezone: zod_1.z.string().default('America/Los_Angeles'),
    pickStrategy: exports.WrPickStrategySchema.default('worst_first'),
    categoryIds: zod_1.z.array(zod_1.z.string()).default([]),
    ownerSpotCheck: zod_1.z.boolean().default(false),
    rollOverUnfinished: zod_1.z.boolean().default(true),
    publishOnlineWhenReady: zod_1.z.boolean().default(false),
    redFlagRules: zod_1.z.record(zod_1.z.boolean()).default({}),
    paused: zod_1.z.boolean().default(false),
    /**
     * 'fixed' = assigneeIds on `days` at assignHour (original behaviour).
     * 'shifts' = only assigneeIds with a PUBLISHED shift that day, assigned from
     * their shift start; batch = shift hours × itemsPerShiftHour.
     */
    scheduleMode: zod_1.z.enum(['fixed', 'shifts']).default('fixed'),
    itemsPerShiftHour: zod_1.z.number().min(0.5).max(10).default(1.5),
    /** Per-person batch size override (userId → items), both modes. */
    perPersonItems: zod_1.z.record(zod_1.z.number().int().min(1).max(50)).default({}),
    /** Give `topUpSize` more as soon as someone has nothing open, up to `dailyCap`. */
    autoTopUp: zod_1.z.boolean().default(true),
    topUpSize: zod_1.z.number().int().min(1).max(20).default(4),
    dailyCap: zod_1.z.number().int().min(1).max(100).default(20),
    notifications: exports.WrNotificationSettingsSchema.default({}),
    /**
     * Product editor in Web Readiness mode: false (default) = "Mark web ready
     * only" — Publish / Archive / Delete are hidden; changes waiting for Publish
     * go to review and the approving manager publishes. true = employees also see
     * the normal Publish button.
     */
    employeeCanPublish: zod_1.z.boolean().default(false),
});
function defaultWrSettings() {
    return exports.WrSettingsSchema.parse({});
}
exports.WrSummarySchema = zod_1.z.object({
    total: zod_1.z.number().int(),
    webReady: zod_1.z.number().int(),
    awaitingReview: zod_1.z.number().int(),
    inProgress: zod_1.z.number().int(),
    notStarted: zod_1.z.number().int(),
    excluded: zod_1.z.number().int(),
    dueToday: zod_1.z.number().int(),
    redFlags: zod_1.z.number().int(),
    perDayCapacity: zod_1.z.number().int(),
    /** ISO date the catalog finishes at the current pace, when computable. */
    estimatedFinish: zod_1.z.string().optional(),
    issueCounts: zod_1.z.record(zod_1.z.number().int()),
    fixedThisWeek: zod_1.z.object({
        photosAdded: zod_1.z.number().int(),
        descriptionsChanged: zod_1.z.number().int(),
        titlesChanged: zod_1.z.number().int(),
        categoriesSet: zod_1.z.number().int(),
    }),
    lastScanAt: zod_1.z.string().optional(),
});
exports.WrScorecardSchema = zod_1.z.object({
    userId: zod_1.z.string(),
    userName: zod_1.z.string(),
    ticked: zod_1.z.number().int(),
    assigned: zod_1.z.number().int(),
    redFlags: zod_1.z.number().int(),
    photos: zod_1.z.number().int(),
    avgMinutesPerItem: zod_1.z.number(),
    /** % of their web-ready items still passing the nightly re-check. */
    stillPassingPct: zod_1.z.number(),
});
exports.WrSubmitInputSchema = zod_1.z.object({
    confirmations: zod_1.z.array(zod_1.z.string()).default([]),
    overrides: zod_1.z.array(zod_1.z.object({ code: exports.WrIssueCodeSchema, reason: zod_1.z.string().min(3) })).default([]),
    note: zod_1.z.string().max(500).optional(),
    /**
     * The product has changes that only go live on Publish (never published, or
     * location prices / online switch saved as a draft). From Web Readiness the
     * employee never publishes — the item goes to review and the approving
     * manager publishes.
     */
    needsPublish: zod_1.z.boolean().optional(),
});
exports.WrClientEventInputSchema = zod_1.z.object({
    type: zod_1.z.enum(['saved', 'photo_added']),
    photos: zod_1.z.array(zod_1.z.object({
        url: zod_1.z.string(),
        variantId: zod_1.z.string().optional(),
        source: zod_1.z.enum(['camera', 'library', 'unknown']).default('unknown'),
    })).optional(),
});
/** Manual confirmations the employee ticks themselves (not machine-checkable). */
exports.WR_CONFIRMATIONS = [
    { key: 'description_accurate', label: 'Description is accurate for this product' },
    { key: 'photos_match', label: 'Each photo matches its variant' },
    { key: 'price_online_ok', label: 'Price & online details are correct' },
];
// ── Tool runs (generic plug-in contract) ──────────────────────────────────────
// A run = input → preview (one item per product) → apply → report.
// Tables: {prefix}-tool-runs-{stage} (PK tenantId, SK runId),
//         {prefix}-tool-run-items-{stage} (PK runId, SK productId).
exports.CONVERTIBLE_TYPE_IDS = [
    'type_product_with_variants', 'type_single_item', 'type_apparel', 'type_weighted', 'type_non_scannable',
];
exports.ConvertibleTypeIdSchema = zod_1.z.enum(exports.CONVERTIBLE_TYPE_IDS);
exports.ToolRunStatusSchema = zod_1.z.enum(['previewing', 'ready', 'applying', 'done', 'failed']);
exports.ToolRunSourceSchema = zod_1.z.enum(['direct', 'web-readiness', 'workflow', 'tool']);
exports.ToolRunItemStateSchema = zod_1.z.enum(['change', 'brand', 'ok', 'blocked']);
exports.ToolRunItemResultSchema = zod_1.z.enum(['pending', 'done', 'failed', 'stale', 'skipped']);
exports.ChangeTypeInputSchema = zod_1.z.object({
    productIds: zod_1.z.array(zod_1.z.string().min(1)).min(1).max(500),
    target: exports.ConvertibleTypeIdSchema,
    weightUnit: zod_1.z.enum(['lb', 'kg', 'oz', 'g']).optional(),
    /** variantId → { Color, Size } for a Clothing target; filled from the preview. */
    apparelMap: zod_1.z.record(zod_1.z.object({ Color: zod_1.z.string(), Size: zod_1.z.string() })).optional(),
}).refine((v) => v.target !== 'type_weighted' || !!v.weightUnit, { message: 'weightUnit required', path: ['weightUnit'] });
exports.NameStandardInputSchema = zod_1.z.object({
    /** Omit = every eligible product of the tenant. */
    productIds: zod_1.z.array(zod_1.z.string().min(1)).max(20000).optional(),
    rules: zod_1.z.object({
        genericWhenNoBrand: zod_1.z.boolean().default(true),
        skipRepeatBrand: zod_1.z.boolean().default(true),
        variantNamesFromOptions: zod_1.z.boolean().default(true),
    }).default({}),
    /** Brand row of the tenant's "Generic" brand (platform-app ensures it exists). */
    genericBrandId: zod_1.z.string().optional(),
});
// ── Product merge (Product Conversion · Combine) ──────────────────────────────
exports.MERGE_ITEM_KEY = 'merge';
exports.MERGE_MAX_VARIANTS = 300;
exports.MERGE_STEPS = [
    // indexNew BEFORE removePos/removeWeb: a variant may show twice for seconds, never zero times.
    'check', 'prepare', 'move', 'barcodes', 'locations', 'documents',
    'promotions', 'indexNew', 'removePos', 'removeWeb', 'archive', 'verify',
];
const levelName = zod_1.z.string().trim().min(1).max(20);
exports.MergeSourceSchema = zod_1.z.object({
    productId: zod_1.z.string().min(1),
    /** Omit = every live variant of the product. */
    variantIds: zod_1.z.array(zod_1.z.string().min(1)).min(1).max(exports.MERGE_MAX_VARIANTS).optional(),
});
exports.MergeTargetSchema = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({
        kind: zod_1.z.literal('new'),
        name: zod_1.z.string().trim().min(1).max(200),
        brandId: zod_1.z.string().optional(),
        categoryId: zod_1.z.string().optional(),
        levels: zod_1.z.array(levelName).min(1).max(3),
    }),
    zod_1.z.object({ kind: zod_1.z.literal('existing'), productId: zod_1.z.string().min(1) }),
]);
exports.MergeInputSchema = zod_1.z.object({
    mode: zod_1.z.literal('combine'),
    sources: zod_1.z.array(exports.MergeSourceSchema).min(1).max(100),
    target: exports.MergeTargetSchema,
}).superRefine((v, ctx) => {
    const ids = v.sources.map((s) => s.productId);
    if (new Set(ids).size !== ids.length)
        ctx.addIssue({ code: 'custom', path: ['sources'], message: 'source listed twice' });
    if (v.target.kind === 'existing' && ids.includes(v.target.productId))
        ctx.addIssue({ code: 'custom', path: ['target'], message: 'target cannot be a source' });
    if (v.target.kind === 'new' && new Set(v.target.levels.map((l) => l.toLowerCase())).size !== v.target.levels.length) {
        ctx.addIssue({ code: 'custom', path: ['target', 'levels'], message: 'level names must differ' });
    }
});
exports.MergeRowSchema = zod_1.z.object({
    variantId: zod_1.z.string(),
    fromProductId: zod_1.z.string(),
    fromName: zod_1.z.string(),
    variantName: zod_1.z.string(),
    values: zod_1.z.record(zod_1.z.string()),
    /** true = every value came from an option key or a rule, not leftover text. */
    certain: zod_1.z.boolean(),
    match: zod_1.z.enum(['matched', 'check', 'duplicate', 'empty', 'invalid']),
});
exports.MergeConflictSchema = zod_1.z.object({
    field: zod_1.z.string(),
    label: zod_1.z.string(),
    options: zod_1.z.array(zod_1.z.object({ productId: zod_1.z.string(), productName: zod_1.z.string(), display: zod_1.z.string() })),
    /** productId whose value the new product takes. */
    chosen: zod_1.z.string(),
});
exports.MergePromotionSchema = zod_1.z.object({
    promotionId: zod_1.z.string(), name: zod_1.z.string(),
    sourceProductIds: zod_1.z.array(zod_1.z.string()),
    choice: zod_1.z.enum(['repoint', 'leave']),
});
exports.MergeStepSchema = zod_1.z.object({
    key: zod_1.z.enum(exports.MERGE_STEPS),
    status: zod_1.z.enum(['pending', 'running', 'done', 'failed', 'skipped']),
    done: zod_1.z.number().int().optional(), total: zod_1.z.number().int().optional(),
    note: zod_1.z.string().optional(), at: zod_1.z.string().optional(),
});
exports.MergeIntegritySchema = zod_1.z.object({
    oldPosDocs: zod_1.z.number().int(), oldWebDocs: zod_1.z.number().int(),
    duplicateVariantIds: zod_1.z.array(zod_1.z.string()), missingPosDocs: zod_1.z.array(zod_1.z.string()),
    staleBarcodeRows: zod_1.z.number().int(), staleLocationRows: zod_1.z.number().int(),
    variantsNotMoved: zod_1.z.array(zod_1.z.string()), sourcesNotArchived: zod_1.z.array(zod_1.z.string()),
    clean: zod_1.z.boolean(),
    /** Moved variants that had a web doc before the merge but have no target web doc now. */
    missingWebDocs: zod_1.z.array(zod_1.z.string()).optional(),
    /** Fully-moved sources still 'archiving' under THIS run (the archive flow is in flight). */
    archivingInProgress: zod_1.z.array(zod_1.z.string()).optional(),
});
exports.MergePlanSchema = zod_1.z.object({
    levels: zod_1.z.array(zod_1.z.string()),
    target: exports.MergeTargetSchema,
    /** Set by step `prepare` (new) or copied from input (existing). */
    targetProductId: zod_1.z.string().optional(),
    targetName: zod_1.z.string(),
    /** Existing target's combination keys (comboKey) — rows may not land on these. */
    takenCombos: zod_1.z.array(zod_1.z.string()),
    sources: zod_1.z.array(zod_1.z.object({
        productId: zod_1.z.string(), name: zod_1.z.string(),
        updatedAt: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]).optional(),
        liveVariantIds: zod_1.z.array(zod_1.z.string()),
        movingAll: zod_1.z.boolean(),
    })),
    targetUpdatedAt: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]).optional(),
    rows: zod_1.z.array(exports.MergeRowSchema),
    conflicts: zod_1.z.array(exports.MergeConflictSchema),
    /** Product-level fields all sources agree on — copied silently, listed on Review. */
    copied: zod_1.z.array(zod_1.z.string()),
    promotions: zod_1.z.array(exports.MergePromotionSchema),
    steps: zod_1.z.array(exports.MergeStepSchema),
    integrity: exports.MergeIntegritySchema.optional(),
    repairs: zod_1.z.number().int().optional(),
    /** Existing target's live variant ids at preview (sorted) — apply fails as stale if they changed. */
    targetVariantIds: zod_1.z.array(zod_1.z.string()).optional(),
    /** Moved variants whose old web doc was actually removed — each must have a target web doc. */
    webVariantIds: zod_1.z.array(zod_1.z.string()).optional(),
    /** Verify passes spent waiting for the sources' archive flow (not repairs). */
    archiveWaits: zod_1.z.number().int().optional(),
});
exports.ToolRunCreateInputSchema = zod_1.z.discriminatedUnion('toolKey', [
    zod_1.z.object({ toolKey: zod_1.z.literal(exports.TOOL_CONVERT_TYPE), input: exports.ChangeTypeInputSchema, source: exports.ToolRunSourceSchema.default('direct') }),
    zod_1.z.object({ toolKey: zod_1.z.literal(exports.TOOL_NAME_STANDARD), input: exports.NameStandardInputSchema, source: exports.ToolRunSourceSchema.default('direct') }),
    zod_1.z.object({ toolKey: zod_1.z.literal(exports.TOOL_PRODUCT_CONVERSION), input: exports.MergeInputSchema, source: exports.ToolRunSourceSchema.default('direct') }),
]);
exports.ToolRunCountsSchema = zod_1.z.object({
    total: zod_1.z.number().int(), change: zod_1.z.number().int(), brand: zod_1.z.number().int(), ok: zod_1.z.number().int(), blocked: zod_1.z.number().int(),
    included: zod_1.z.number().int(), variants: zod_1.z.number().int(),
    done: zod_1.z.number().int(), failed: zod_1.z.number().int(), stale: zod_1.z.number().int(),
});
exports.ToolRunSchema = zod_1.z.object({
    tenantId: zod_1.z.string(), runId: zod_1.z.string(), toolKey: zod_1.z.string(),
    status: exports.ToolRunStatusSchema, source: exports.ToolRunSourceSchema,
    input: zod_1.z.record(zod_1.z.unknown()),
    counts: exports.ToolRunCountsSchema,
    summary: zod_1.z.string().optional(),
    error: zod_1.z.string().optional(),
    createdBy: zod_1.z.string(), createdByName: zod_1.z.string().optional(),
    createdAt: zod_1.z.string(), appliedAt: zod_1.z.string().optional(), finishedAt: zod_1.z.string().optional(),
});
exports.ToolRunVariantChangeSchema = zod_1.z.object({
    variantId: zod_1.z.string(),
    before: zod_1.z.record(zod_1.z.unknown()), after: zod_1.z.record(zod_1.z.unknown()),
    /** Display copy for the report ("Code Red" → "Flavour: Code Red"). */
    label: zod_1.z.string().optional(),
});
exports.ToolRunItemSchema = zod_1.z.object({
    runId: zod_1.z.string(), productId: zod_1.z.string(),
    state: exports.ToolRunItemStateSchema,
    include: zod_1.z.boolean(),
    note: zod_1.z.string().optional(),
    productBefore: zod_1.z.record(zod_1.z.unknown()),
    productAfter: zod_1.z.record(zod_1.z.unknown()),
    variants: zod_1.z.array(exports.ToolRunVariantChangeSchema),
    /** Freshness guard: product.updatedAt (epoch ms on products; string tolerated) + live variant ids at preview time. */
    plannedFrom: zod_1.z.object({ updatedAt: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]).optional(), variantIds: zod_1.z.array(zod_1.z.string()) }),
    result: exports.ToolRunItemResultSchema,
    error: zod_1.z.string().optional(),
    /** product-conversion only: the whole merge (one item per run, productId = 'merge'). */
    merge: exports.MergePlanSchema.optional(),
});
exports.ToolRunItemPatchSchema = zod_1.z.object({
    include: zod_1.z.boolean().optional(),
    /** Name Standard "Needs brand" row: pick a brand → item is re-planned. */
    brandId: zod_1.z.string().optional(),
    /** Convert to Clothing: fix one variant's Colour/Size → item is re-planned. */
    apparel: zod_1.z.record(zod_1.z.object({ Color: zod_1.z.string(), Size: zod_1.z.string() })).optional(),
    /** Merge: variantId → { level: value } edits from the Arrange table. */
    mergeValues: zod_1.z.record(zod_1.z.record(zod_1.z.string())).optional(),
    /** Merge: field → productId whose value the new product takes. */
    conflictChoices: zod_1.z.record(zod_1.z.string()).optional(),
    /** Merge: promotionId → repoint | leave. */
    promotionChoices: zod_1.z.record(zod_1.z.enum(['repoint', 'leave'])).optional(),
});
//# sourceMappingURL=tools.schema.js.map